import React, { useState, useEffect, useMemo } from "react";
import Footer from "../components/Navbars/footer";
import {
  Plus,
  Edit2,
  Trash2,
  Loader2,
  MapPin,
  Building2,
  Search,
  Filter,
  AlertCircle,
  X,
  Crosshair,
} from "lucide-react";
import {
  useOfficeBuildings,
  useGetBusinessUnits,
  useCreateOfficeBuilding,
  useUpdateOfficeBuilding,
  useDeleteOfficeBuilding,
} from "../adapters/Requests";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const Badge = ({ children, color = "gray" }) => {
  const styles = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-50 text-green-700 border-green-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles[color]}`}
    >
      {children}
    </span>
  );
};

const BuildingSettings = () => {
  const {
    data: buildings = [],
    isLoading: isLoadingBuildings,
    isFetching: isFetchingBuildings,
  } = useOfficeBuildings();
  const { data: businessUnitsData } = useGetBusinessUnits();
  const businessUnits = Array.isArray(businessUnitsData)
    ? businessUnitsData
    : businessUnitsData?.businessUnits || [];

  const createBuilding = useCreateOfficeBuilding();
  const updateBuilding = useUpdateOfficeBuilding();
  const deleteBuilding = useDeleteOfficeBuilding();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState("All");

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    business_unit_code: "",
    latitude: "",
    longitude: "",
    radius: 0.12,
  });

  const filteredBuildings = useMemo(() => {
    return buildings.filter((b) => {
      const matchesSearch =
        b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (b.address &&
          b.address.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesBusinessUnit =
        selectedBusinessUnit === "All" ||
        b.business_unit?.name === selectedBusinessUnit;
      return matchesSearch && matchesBusinessUnit;
    });
  }, [buildings, searchQuery, selectedBusinessUnit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBuilding) {
        await updateBuilding.mutateAsync({
          id: editingBuilding.id,
          ...formData,
        });
        toast.success("Location updated successfully");
      } else {
        await createBuilding.mutateAsync(formData);
        toast.success("Location created successfully");
      }
      closeModal();
    } catch (error) {
      console.error("Error saving building", error);
      toast.error(error.response?.data?.error || "Failed to save building");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This action cannot be undone.")) return;
    try {
      await deleteBuilding.mutateAsync(id);
      toast.success("Location deleted successfully");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to delete building");
    }
  };

  const openEdit = (building) => {
    setEditingBuilding(building);
    setFormData({
      name: building.name,
      address: building.address || "",
      business_unit_code: building.business_unit_code,
      latitude: building.latitude,
      longitude: building.longitude,
      radius: building.radius,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingBuilding(null);
    setFormData({
      name: "",
      address: "",
      business_unit_code: "",
      latitude: "",
      longitude: "",
      radius: 0.12,
    });
  };

  const isSubmitting = createBuilding.isPending || updateBuilding.isPending;

  return (
    <>
      <div className="w-full max-w-[95%] 2xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg shrink-0">
                <Building2 className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                  Office Locations
                </h1>
                <p className="text-sm text-gray-500">
                  Manage building coordinates and access zones
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                closeModal();
                setIsModalOpen(true);
              }}
              className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors text-sm font-medium shadow-sm"
            >
              <Plus size={16} />
              Add Building
            </button>
          </div>

          <div className="p-3 bg-gray-50/50 flex flex-col sm:flex-row gap-3 items-center rounded-b-xl">
            <div className="relative flex-1 w-full">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm bg-white"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter size={16} className="text-gray-400" />
              <Select
                value={selectedBusinessUnit}
                onValueChange={(value) => setSelectedBusinessUnit(value)}
              >
                <SelectTrigger className="w-full sm:w-48 bg-white border border-gray-200">
                  <SelectValue placeholder="All Business Units" />
                </SelectTrigger>
                <SelectContent style={{ maxHeight: "80vh", height: "20rem" }}>
                  <SelectItem value="All">All Business Units</SelectItem>
                  {businessUnits.map((bu) => (
                    <SelectItem key={bu.id} value={bu.name}>
                      {bu.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
          {isFetchingBuildings && !isLoadingBuildings && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
            </div>
          )}
          {isLoadingBuildings ? (
            <div className="flex flex-col items-center justify-center py-20 text-green-700">
              <Loader2 className="animate-spin w-8 h-8 mb-2" />
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          ) : filteredBuildings.length === 0 ? (
            <div className="text-center py-20">
              <MapPin className="text-gray-300 w-12 h-12 mx-auto mb-3" />
              <h3 className="text-gray-900 font-medium">No locations found</h3>
              <p className="text-gray-500 text-sm">
                Try adjusting your filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold">
                    <th className="py-3 px-4">Building Name</th>
                    <th className="py-3 px-4">Business Unit</th>
                    <th className="py-3 px-4">Address</th>
                    <th className="py-3 px-4">Geo-Data</th>
                    <th className="py-3 px-4 text-center">Interns</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredBuildings.map((building) => (
                    <tr
                      key={building.id}
                      className="hover:bg-gray-50 transition-colors group"
                    >
                      <td className="py-3 px-4 font-medium text-gray-900">
                        {building.name}
                      </td>
                      <td className="py-3 px-4">
                        <Badge color="green">
                          {building.business_unit?.name || "N/A"}
                        </Badge>
                      </td>
                      <td
                        className="py-3 px-4 text-gray-600 max-w-xs truncate"
                        title={building.address}
                      >
                        {building.address || "—"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 font-mono">
                          <Crosshair size={12} className="text-gray-400" />
                          <span>
                            {building.latitude?.toFixed(4)},{" "}
                            {building.longitude?.toFixed(4)}
                          </span>
                          <span className="text-gray-300 mx-1">|</span>
                          <span>{building.radius}km</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge color="gray">
                          {building._count?.placements || 0}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(building)}
                            className="p-1.5 text-gray-400 hover:text-green-700 hover:bg-green-50 rounded-md transition-all"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(building.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <Footer />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
            onClick={closeModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="font-semibold text-gray-900">
                {editingBuilding ? "Edit Location" : "New Location"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Building Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500 outline-none text-sm transition-shadow"
                    placeholder="e.g. Headquarters Block A"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Business Unit
                    </label>
                    <Select
                      required
                      value={formData.business_unit_code}
                      onValueChange={(value) =>
                        setFormData({ ...formData, business_unit_code: value })
                      }
                    >
                      <SelectTrigger className="w-full bg-white border border-gray-300 truncate">
                        <SelectValue placeholder="Select BU..." />
                      </SelectTrigger>
                      <SelectContent
                        style={{ maxHeight: "80vh", height: "20rem" }}
                      >
                        {businessUnits.map((bu) => (
                          <SelectItem key={bu.id} value={bu.code}>
                            {bu.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Radius (km)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.radius}
                      onChange={(e) =>
                        setFormData({ ...formData, radius: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500 outline-none text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500 outline-none text-sm"
                    placeholder="Physical address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="col-span-2 flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <MapPin size={12} />
                    <span className="font-semibold">Coordinates</span>
                  </div>
                  <div>
                    <input
                      type="number"
                      step="any"
                      required
                      value={formData.latitude}
                      onChange={(e) =>
                        setFormData({ ...formData, latitude: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500 outline-none text-sm bg-white"
                      placeholder="Latitude"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      step="any"
                      required
                      value={formData.longitude}
                      onChange={(e) =>
                        setFormData({ ...formData, longitude: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500 outline-none text-sm bg-white"
                      placeholder="Longitude"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmitting && (
                      <Loader2 className="animate-spin w-4 h-4" />
                    )}
                    {editingBuilding ? "Save Changes" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BuildingSettings;
